import React from 'react';

export interface MascotPalette {
    primaryHat?: string;
    hatBand?: string;
    hatBandAccentGold?: string;
    hatBandAccentWhite?: string;
    hatPom?: string;
    skin?: string;
    blush?: string;
    hair?: string;
    outfit?: string;
    collar?: string;
    embroidery?: string;
    embroideryAccent?: string;
}

export interface HmongChildMascotProps {
    size?: number;
    title?: string;
    className?: string;
    style?: React.CSSProperties;
    palette?: MascotPalette;
    variant?: 'white' | 'green';
}

// Simple, friendly Hmong child cartoon mascot as a self-contained SVG
// Focuses on recognizable cultural elements: hat (phuam), bright colors, simple geometric patterns
export function HmongChildMascot(props: HmongChildMascotProps) {
    const { size = 60, title = 'Hmong child mascot', className, style, palette, variant } = props;

    const c: Required<MascotPalette> = {
        primaryHat: '#D32F2F',
        hatBand: '#1B1B1B',
        hatBandAccentGold: '#FFD54F',
        hatBandAccentWhite: '#FFFFFF',
        hatPom: '#2ECC71',
        skin: '#F7C7A8',
        blush: '#F2A3A3',
        hair: '#2f2f2f',
        outfit: '#1E88E5',
        collar: '#E3F2FD',
        embroidery: '#2ECC71',
        embroideryAccent: '#FFFFFF',
        ...(palette || {})
    } as Required<MascotPalette>;

    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 120 120"
            role="img"
            aria-label={title}
            className={className}
            style={{ display: 'block', pointerEvents: 'none', ...style }}
        >
            <title>{title}</title>

            {/* Background soft shadow */}
            <ellipse cx="60" cy="102" rx="28" ry="8" fill="rgba(0,0,0,0.10)" />

            {/* Neck */}
            <rect x="55" y="64" width="10" height="8" rx="4" fill={c.skin} />

            {/* Face */}
            <circle cx="60" cy="50" r="22" fill={c.skin} stroke={c.hair} strokeWidth="1" />

            {/* Blush */}
            <ellipse cx="48" cy="54" rx="4.5" ry="2.5" fill={c.blush} opacity="0.65" />
            <ellipse cx="72" cy="54" rx="4.5" ry="2.5" fill={c.blush} opacity="0.65" />

            {/* Eyes */}
            <circle cx="52" cy="48" r="3.2" fill={c.hair} />
            <circle cx="68" cy="48" r="3.2" fill={c.hair} />
            {/* Eye sparkle */}
            <circle cx="51.2" cy="47.3" r="0.8" fill="#ffffff" />
            <circle cx="67.2" cy="47.3" r="0.8" fill="#ffffff" />

            {/* Smile */}
            <path d="M48 58 Q60 64 72 58" fill="none" stroke={c.hair} strokeWidth="2.2" strokeLinecap="round" />

            {/* Simple child hair fringe under hat */}
            <path d="M42 40 Q60 34 78 40 Q75 42 72 42 Q60 39 48 42 Q45 42 42 40" fill={c.hair} />

            {/* Traditional Hmong child hat (phuam) with variant details */}
            {variant === 'white' ? (
                <g>
                    {/* White Hmong: darker base cap with white band and coins */}
                    <path d="M38 36 Q60 26 82 36 L82 38 Q60 33 38 38 Z" fill="#111111" stroke="#ffffff" strokeWidth="1.2" />
                    <rect x="40" y="36" width="40" height="6" fill="#ffffff" opacity="0.95" />
                    {Array.from({ length: 8 }).map((_: any, i) => (
                        <circle key={`coin-${i}`} cx={42 + i * 5} cy={39} r={1.6} fill="#d9d9d9" stroke="#bfbfbf" strokeWidth="0.5" />
                    ))}
                </g>
            ) : variant === 'green' ? (
                <g>
                    {/* Green Hmong: taller, pointed style with green band and sawtooth pattern */}
                    <path d="M42 40 L60 24 L78 40 L78 42 Q60 38 42 42 Z" fill="#2e7d32" stroke="#e8f5e9" strokeWidth="1.2" />
                    <rect x="44" y="40" width="32" height="6" fill="#2ECC71" opacity="0.95" />
                    <path d="M46 46 L48 40 L50 46 M50 46 L52 40 L54 46 M54 46 L56 40 L58 46 M58 46 L60 40 L62 46 M62 46 L64 40 L66 46 M66 46 L68 40 L70 46 M70 46 L72 40 L74 46" stroke="#ffffff" strokeWidth="1.2" fill="none" />
                </g>
            ) : (
                <g>
                    <path d="M38 36 Q60 24 82 36 L82 38 Q60 33 38 38 Z" fill={c.primaryHat} stroke={c.hatBandAccentWhite} strokeWidth="1.5" />
                    <rect x="40" y="36" width="40" height="5" fill={c.hatBand} opacity="0.85" />
                    <path d="M42 41 L46 36 L50 41 M50 41 L54 36 L58 41 M58 41 L62 36 L66 41 M66 41 L70 36 L74 41" stroke={c.hatBandAccentGold} strokeWidth="1.2" fill="none" />
                    <path d="M44 41 L48 36 L52 41 M52 41 L56 36 L60 41 M60 41 L64 36 L68 41" stroke={c.hatBandAccentWhite} strokeWidth="1.2" fill="none" opacity="0.9" />
                    <circle cx="60" cy="30" r="3" fill={c.hatPom} stroke={c.hatBandAccentWhite} strokeWidth="1" />
                </g>
            )}

            {/* Torso with paj ntaub inspired panel */}
            <rect x="40" y="70" width="40" height="34" rx="8" fill={c.outfit} />
            {/* Collar */}
            <path d="M50 70 Q60 76 70 70" fill={c.collar} />
            {/* Embroidery band */}
            <rect x="44" y="86" width="32" height="8" fill={c.embroidery} />
            {/* Simple diamond pattern */}
            <path d="M48 90 L50 88 L52 90 L50 92 Z" fill={c.embroideryAccent} />
            <path d="M54 90 L56 88 L58 90 L56 92 Z" fill={c.embroideryAccent} />
            <path d="M60 90 L62 88 L64 90 L62 92 Z" fill={c.embroideryAccent} />
            <path d="M66 90 L68 88 L70 90 L68 92 Z" fill={c.embroideryAccent} />

            {/* Arms (peeking) */}
            <circle cx="42" cy="84" r="5" fill={c.skin} />
            <circle cx="78" cy="84" r="5" fill={c.skin} />
        </svg>
    );
}

export default HmongChildMascot;


